import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Collapse } from "@mui/material";
import BasicTable from "../../../Components/Table/Material";
import useApi from "../../../Hooks/useApi";
import Tooltip from "@mui/material/Tooltip";
import { usePagination } from "../../../Hooks/usePagination";

import { SVG_DATA_UPLOAD, SVG_NDF } from "../../../Assets/SVG/svg_util";
import Common from "../../../Components/Layouts/Common";
import showToast from "../../../Helpers/toast";
import TextField from "../../../Components/UI/Inputs/TextField";
import DropDown from "../../../Components/UI/Inputs/DropDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faXmark, faFilter } from "@fortawesome/free-solid-svg-icons";
import { sortRows, nextSortConfig } from "../../../Helpers/tableSort";

// Build dropdown options from whatever distinct values are actually present
// in the loaded data, since the exact status enum isn't documented in the API.
const buildOptionsFromValues = (rows, key) => {
  const distinct = Array.from(
    new Set((rows || []).map((row) => row[key]).filter(Boolean))
  );
  return [
    { name: "All", value: "" },
    ...distinct.map((value) => ({ name: value, value })),
  ];
};

// =========================
// NDF STATUS HELPERS
// =========================

/**
 * All values that mean "NDF submission is fully done".
 * Covers whichever value the backend eventually settles on.
 */
const NDF_DONE_STATUSES = ["Success", "Completed", "Submitted"];

const isNdfDone = (ndfStatus) => NDF_DONE_STATUSES.includes(ndfStatus);

const isNdfInProgress = (ndfStatus) => ndfStatus === "In_Progress";

const isNdfFailed = (ndfStatus) => ndfStatus === "Failed";

// =========================
// NDF STATUS OVERLAY ICONS
// =========================

const GreenTickOverlay = () => (
  <span
    className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-500 flex items-center justify-center shadow"
    title="Completed"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12 12"
      fill="none"
      className="w-2.5 h-2.5"
    >
      <path
        d="M2 6.5L4.5 9L10 3.5"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const TimerOverlay = () => (
  <span
    className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-yellow-400 flex items-center justify-center shadow"
    title="In Progress"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12 12"
      fill="none"
      className="w-2.5 h-2.5"
    >
      <circle cx="6" cy="6.5" r="4.5" stroke="white" strokeWidth="1.5" />
      <path
        d="M6 4V6.5L7.5 7.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4.5 1H7.5"
        stroke="white"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  </span>
);

const RedCrossOverlay = () => (
  <span
    className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-red-500 flex items-center justify-center shadow"
    title="Failed"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 12 12"
      fill="none"
      className="w-2.5 h-2.5"
    >
      <path
        d="M3 3L9 9M9 3L3 9"
        stroke="white"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  </span>
);

const RetryIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    className="w-4 h-4"
  >
    <path
      d="M4 4v5h5M20 20v-5h-5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5.5 9A7.5 7.5 0 0 1 19 8.5M18.5 15A7.5 7.5 0 0 1 5 15.5"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// =========================
// NDF BUTTON HELPERS
// =========================

/**
 * Clickable only when data has been uploaded AND NDF submission
 * has not started, is not already completed, is not in progress,
 * and has not failed. A failed submission is handled by the
 * separate retry button, not the main button.
 */
const isNdfClickable = (row) =>
  row.status === "SUBMITTED" &&
  !isNdfDone(row.ndfStatus) &&
  !isNdfInProgress(row.ndfStatus) &&
  !isNdfFailed(row.ndfStatus);

const getNdfBgColor = (row) =>
  isNdfClickable(row) ? "bg-[#1976d2]" : "bg-gray-400 cursor-not-allowed";

const getNdfTooltip = (row) => {
  if (isNdfDone(row.ndfStatus)) return "Already submitted to NDF";
  if (isNdfInProgress(row.ndfStatus)) return "NDF submission in progress";
  if (isNdfFailed(row.ndfStatus)) return "NDF submission failed";
  if (row.status !== "SUBMITTED")
    return "Upload data first before submitting to NDF";
  return "Submit to NDF";
};

const SurveyDataSubmissionCityList = () => {
  const { request } = useApi();
  const navigate = useNavigate();
  const paginateProps = usePagination();

  const location = useLocation();
  const { surveyId, surveyName, questionId } = location?.state || {};

  const [surveyCityList, setSurveyCityList] = useState([]);
  const [data, setData] = useState(null);

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [ndfStatusFilter, setNdfStatusFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const statusOptions = useMemo(
    () => buildOptionsFromValues(surveyCityList, "status"),
    [surveyCityList]
  );
  const ndfStatusOptions = useMemo(
    () => buildOptionsFromValues(surveyCityList, "ndfStatus"),
    [surveyCityList]
  );

  const handleSort = (key) => {
    setSortConfig((prev) => nextSortConfig(prev, key));
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("");
    setNdfStatusFilter("");
    paginateProps.setPage(0);
  };

  // Full dataset is already loaded in one shot by the API (no server-side
  // search/filter/sort/pagination params exist for this endpoint), so all of
  // this can safely run entirely client-side against the full array.
  const filteredSortedData = useMemo(() => {
    let rows = surveyCityList || [];

    if (searchTerm.trim()) {
      const term = searchTerm.trim().toLowerCase();
      rows = rows.filter((row) => row.name?.toLowerCase().includes(term));
    }
    if (statusFilter) {
      rows = rows.filter((row) => row.status === statusFilter);
    }
    if (ndfStatusFilter) {
      rows = rows.filter((row) => row.ndfStatus === ndfStatusFilter);
    }

    return sortRows(rows, sortConfig);
  }, [surveyCityList, searchTerm, statusFilter, ndfStatusFilter, sortConfig]);

  useEffect(() => {
    paginateProps.setTotalData(filteredSortedData.length);
  }, [filteredSortedData]);

  useEffect(() => {
    paginateProps.setPage(0);
  }, [searchTerm, statusFilter, ndfStatusFilter]);

  const paginatedData = useMemo(() => {
    const start = paginateProps.page * paginateProps.rowsPerPage;
    return filteredSortedData.slice(start, start + paginateProps.rowsPerPage);
  }, [filteredSortedData, paginateProps.page, paginateProps.rowsPerPage]);

  // =========================
  // DATA UPLOAD BUTTON CONFIG
  // =========================
  const dataUploadConfig = {
    Icon: SVG_DATA_UPLOAD,
    tooltip: (row) =>
      row.status === "SUBMITTED" ? "Already Uploaded" : "Upload Data",

    bgColor: (row) =>
      row.status === "SUBMITTED"
        ? "bg-gray-400 cursor-not-allowed"
        : "bg-[#1976d2]",

    onClick: (navigate, row) => () => {
      if (row.status !== "SUBMITTED") {
        navigate(`/survey-data-submission`, {
          state: {
            surveyId,
            surveyName,
            city: row.name,
            id: row.id,
            questionId,
            answerType: data?.answerType,
            locationId: row.id,
          },
        });
      }
    },
  };

  // =========================
  // FETCH
  // =========================
  const fetchSurveyCityList = async () => {
    try {
      const response = surveyId
        ? await request(
            "GET",
            `/v1/survey/city-mapping/${surveyId}`,
            {},
            "survey_service",
          )
        : undefined;

      setData(response);
      setSurveyCityList(response?.cities || []);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchSurveyCityList();
  }, []);

  // =========================
  // SUBMIT TO NDF
  // =========================
  const submitToNDF = async (payload) => {
    try {
      const response = await request(
        "POST",
        `/v1/external/ndf-publication/surveys/${payload.surveyId}/locations/${payload.locationId}`,
        {},
        "survey_service",
      );

      showToast({
        message: "Successfully submitted to NDF.",
      });

      if (response) {
        setTimeout(() => {
          fetchSurveyCityList();
        }, 30000);
      }
    } catch (error) {
      console.log(error?.response?.data?.message);
      showToast({
        message: "NDF Submission Failed!",
        type: "error",
      });
    }
  };

  // =========================
  // RETRY NDF SUBMISSION
  // =========================
  const retryNDF = async (payload) => {
    try {
      const response = await request(
        "POST",
        `/v1/external/ndf-publication/surveys/${payload.surveyId}/locations/${payload.locationId}/retry`,
        {},
        "survey_service",
      );

      showToast({
        message: "NDF submission retry initiated.",
      });

      if (response) {
        setTimeout(() => {
          fetchSurveyCityList();
        }, 30000);
      }
    } catch (error) {
      console.log(error?.response?.data?.message);
      showToast({
        message: "NDF Retry Failed!",
        type: "error",
      });
    }
  };

  // =========================
  // COLUMNS
  // =========================
  const columns = [
    {
      id: "name",
      label: "City Name",
      minWidth: 170,
      sortable: true,
    },
    {
      id: "creationDate",
      label: "Assigned Date",
      minWidth: 100,
      sortable: true,
    },
    {
      id: "status",
      label: "Data Submission Status",
      minWidth: 100,
      sortable: true,
    },

    // Action column — data upload
    {
      id: "action",
      label: "Actions",
      minWidth: 170,
      align: "center",
      renderCell: (row) => (
        <div className="inline-flex space-x-2">
          <Tooltip title={dataUploadConfig.tooltip(row)}>
            <button
              onClick={dataUploadConfig.onClick(navigate, row)}
              className={`${dataUploadConfig.bgColor(
                row,
              )} border border-gray-300 h-8 w-8 text-white rounded-md flex items-center justify-center p-1`}
            >
              <SVG_DATA_UPLOAD />
            </button>
          </Tooltip>
        </div>
      ),
    },

    // NDF Submission column
    {
      id: "ndfSubmission",
      sortKey: "ndfStatus",
      label: "NDF Submission",
      minWidth: 170,
      align: "center",
      sortable: true,
      renderCell: (row) => {
        const clickable = isNdfClickable(row);
        const failed = isNdfFailed(row.ndfStatus);

        const handleClick = () => {
          if (clickable) {
            submitToNDF({
              surveyId,
              surveyName,
              locationId: row.id,
              ndfStatus: row.ndfStatus,
              cityName: `${row.name} ${row.type}`,
            });
          }
        };

        const handleRetryClick = () => {
          retryNDF({
            surveyId,
            surveyName,
            locationId: row.id,
            ndfStatus: row.ndfStatus,
            cityName: `${row.name} ${row.type}`,
          });
        };

        return (
          <div className="inline-flex space-x-2">
            <Tooltip title={getNdfTooltip(row)}>
              {/*
                Wrapper span needed because MUI Tooltip won't attach to
                a disabled button. We manage disabled state via onClick guard
                and CSS instead of the native disabled attribute.
              */}
              <span className="relative inline-flex">
                <button
                  onClick={handleClick}
                  disabled={!clickable}
                  className={`
                    ${getNdfBgColor(row)}
                    border border-gray-300
                    h-8 w-8
                    text-white
                    rounded-md
                    flex items-center justify-center
                    p-1
                    transition-all duration-100
                    ${
                      clickable
                        ? "active:scale-90 active:brightness-90 active:shadow-inner"
                        : ""
                    }
                  `}
                >
                  <SVG_NDF />
                </button>

                {/* Status overlays */}
                {isNdfDone(row.ndfStatus) && <GreenTickOverlay />}
                {isNdfInProgress(row.ndfStatus) && <TimerOverlay />}
                {failed && <RedCrossOverlay />}
              </span>
            </Tooltip>

            {/* Retry button — only shown when NDF submission has failed */}
            {failed && (
              <Tooltip title="Retry NDF submission">
                <button
                  onClick={handleRetryClick}
                  className="bg-[#d32f2f] border border-gray-300 h-8 w-8 text-white rounded-md flex items-center justify-center p-1 transition-all duration-100 active:scale-90 active:brightness-90 active:shadow-inner"
                >
                  <RetryIcon />
                </button>
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];

  useEffect(() => {
    if (!surveyId) {
      navigate("/manage-survey");
    }
  }, [surveyId]);

  return (
    <Common headline={"Survey Data Submission City List"}>
      <div className="bg-white relative">
        <div className="flex mb-4">
          <h4 className="font-bold text-gray-800 pr-2">Survey Name:</h4>
          <h5>{surveyName}</h5>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pb-2">
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faFilter} />}
            onClick={() => setIsFilterVisible((prev) => !prev)}
          >
            {isFilterVisible ? "Hide Filters" : "Show Filters"}
          </Button>
          {(searchTerm || statusFilter || ndfStatusFilter) && (
            <Button
              variant="outlined"
              color="secondary"
              startIcon={<FontAwesomeIcon icon={faXmark} />}
              onClick={handleResetFilters}
            >
              Reset Filters
            </Button>
          )}
        </div>

        <Collapse in={isFilterVisible}>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pb-4">
            <TextField
              value={searchTerm}
              name="searchTerm"
              onValueChange={(_, value) => setSearchTerm(value)}
              icon={<FontAwesomeIcon icon={faSearch} />}
              placeholder="Search by city name"
              fullWidth
            />
            <DropDown
              setter={setStatusFilter}
              placeholder="Data Submission Status"
              value={statusFilter}
              options={statusOptions}
              fullWidth
            />
            <DropDown
              setter={setNdfStatusFilter}
              placeholder="NDF Status"
              value={ndfStatusFilter}
              options={ndfStatusOptions}
              fullWidth
            />
          </div>
        </Collapse>

        <BasicTable
          columns={columns}
          data={paginatedData}
          paginateProps={paginateProps}
          sortConfig={sortConfig}
          onSort={handleSort}
        />
      </div>
    </Common>
  );
};

export default SurveyDataSubmissionCityList;
